<template>
  <form-item
    :isBindAttrs="true"
    v-bind="$attrs"
    :name="name"
    :prop="prop"
    :label="getTransText(name,'label',base)"
    :descContent="getTransText(name,'desc',base)"
    >
    <editor
      v-model="val"
      :init="initConf"
      @input="onInput"
      @onChange="onChange"
      :disabled="disabled"
      :placeholder="placeholder"
    >
    </editor>
    <div v-if="isTransField">
      <slot name="prepend-toolbar"></slot>
      <el-button
        v-if="transField.isDef"
        class="trans-action"
        icon="el-icon-copy-document"
        @click="transField.copy(name)"
        >
        {{ $t('api.trans.copyText') }}
        <i class="el-icon-d-arrow-right"></i>
      </el-button>
      <el-checkbox
        v-else
        v-model="transField.data[getStatusField(name)]"
        :true-label="1"
        :false-label="0"
        :disabled="disabled"
        @change="transField.handleCheckChange"
        >
        {{ $t('api.trans.checked') }}
      </el-checkbox>
    </div>
    <div v-if="showGrammarChecker || hasTrans" class="toolbar">
      <el-tooltip
        v-if="showGrammarChecker"
        placement="top-start"
        :content="grammarConfig.tooltipLabel"
        :disabled="!grammarConfig.tooltipLabel || isGrammarCheckerInvalid"
      >
        <el-button
          class="check-grammar-btn"
          type="primary"
          :loading="isLoadingGrammar"
          :disabled="isGrammarCheckerInvalid"
          @click="fetchGrammarChecker"
        >
          <span>{{ grammarConfig.buttonLabel || $t('button_format_check') }}</span>
        </el-button>
      </el-tooltip>

      <el-badge
        v-if="hasTrans"
        class="item trans-checked"
        :is-dot="isTransNeedReview"
      >
        <el-button
          class="trans"
          :type="showTrans ? 'primary' : 'default'"
          @click="toggleTrans"
        >
          <svg-icon icon-class="language"></svg-icon>
          {{ $t('api.translation') }}
          <i :class="showTrans ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        </el-button>
      </el-badge>
    </div>
    <div v-if="showGrammarChecker && formattedGrammarResult" class="ai-grammar-checker-result">
      <editor disabled :init="grammarEditorConfig" :value="formattedGrammarResult" />
      <template v-if="!isLoadingGrammar && !isGrammarCheckerError">
        <el-alert show-icon type="warning" :closable="false" :title="$t('alert_apply_will_replace_content_hint')" />
        <el-button class="apply-grammar-btn" type="primary" @click="onApplyGrammar">
          <span>{{ $t('button_apply_directly') }}</span>
        </el-button>
      </template>
    </div>
    <div
      v-if="hasTrans && showTrans"
      class="translation-section"
    >
      <div v-if="showAutoTranslate" class="translation-section__header">
        <el-alert show-icon type="warning" :closable="false" :title="$t('alert_auto_translate_overwrite_warning')" />
        <el-button
          type="primary"
          :loading="isLoadingTranslation"
          :disabled="!val"
          @click="onGenerateTranslation"
        >
          <span>{{ $t('button_auto_translate') }}</span>
        </el-button>
        <el-alert v-if="isTranslationError" show-icon type="error" :closable="false" :title="$t('alert_data_unavailable')" />
      </div>
      <!-- 使用 v-show 避免顯示 el-card 預設的 border  -->
      <el-card
        class="trans"
        shadow="hover"
        >
        <el-row
          type="flex"
          justify="space-between"
          >
          <el-col :span="6">
            {{ $t('api.trans.all') }}
          </el-col>
          <el-col :span="6"></el-col>
          <el-col :span="6" class="right">
            <el-checkbox
              v-model="checkTransAll"
              :indeterminate="isTransIndeterminate"
              @change="handleCheckTransAllChange"
              >
              {{ $t('api.trans.checked') }}
            </el-checkbox>
          </el-col>
        </el-row>
        <el-collapse v-model="activeNames" accordion>
          <el-collapse-item
            :name="idx"
            v-for="(lang, idx) in transData"
            :key="idx"
            >
            <template #title>
              <div style="width: 100%;">
                <el-row
                  type="flex"
                  justify="space-between"
                  >
                  <el-col :span="6">
                    <el-tag
                      effect="plain"
                      type="info"
                      >
                      <flag-item
                        :id="lang.lang_id"
                        :code="lang.code"
                        >
                      </flag-item>
                    </el-tag>
                  </el-col>
                  <el-col :span="6" :offset="12">
                    <el-checkbox
                      v-model="lang[getStatusField(name)]"
                      :true-label="1"
                      :false-label="0"
                      @change="handleCheckTransChange"
                      >
                      {{ $t('api.trans.checked') }}
                    </el-checkbox>
                  </el-col>
                </el-row>
              </div>
            </template>
            <el-card
              class="trans"
              shadow="hover"
              >
              <editor
                v-model="lang[name]"
                :init="initConf"
                :disabled="disabled || isLoadingTranslation"
                :placeholder="placeholder"
                @input="onInput"
                @onChange="onTransChange"
              >
              </editor>
            </el-card>
          </el-collapse-item>
        </el-collapse>
      </el-card>
    </div>
  </form-item>
</template>

<script>
import { ref, computed, getCurrentInstance } from 'vue'

import { getTransText, getStatusField } from '@/utils/i18n'
import { fetchOne as fetchBranch } from '@/api/merchant/branch'
import { fetchOne as fetchTemplate, fetchList as fetchTemplateList } from '@/api/setting/template'
import { useGrammarChecker } from '@/composables/useGrammarChecker'
import { useTranslationGenerator } from '@/composables/useTranslationGenerator'

import tinymce from 'tinymce/tinymce'
import Editor from '@tinymce/tinymce-vue'
import 'tinymce/icons/default'
import 'tinymce/themes/silver'

import 'tinymce/plugins/advlist'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/image'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/print'
import 'tinymce/plugins/preview'
import 'tinymce/plugins/hr'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/pagebreak'

import 'tinymce/plugins/searchreplace'
import 'tinymce/plugins/wordcount'
import 'tinymce/plugins/visualblocks'
import 'tinymce/plugins/visualchars'
import 'tinymce/plugins/code'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/fullpage'
import 'tinymce/plugins/autoresize'

import 'tinymce/plugins/insertdatetime'
import 'tinymce/plugins/media'
import 'tinymce/plugins/nonbreaking'
import 'tinymce/plugins/noneditable'
import 'tinymce/plugins/save'
import 'tinymce/plugins/table'
import 'tinymce/plugins/directionality'

import 'tinymce/plugins/template'
import 'tinymce/plugins/paste'
import 'tinymce/plugins/textpattern'
import 'tinymce/plugins/imagetools'
import 'tinymce/plugins/toc'
import 'tinymce/plugins/help'
import 'tinymce/plugins/emoticons'

const baseUrl = '/static/tinymce'

const defaultGrammarConfig = {
  content: '',
  currencyCode: '',
  template: '',
  tooltipLabel: '',
  buttonLabel: ''
}

export default {
  components: {
    Editor
  },
  name: 'tinymce-field',
  props: {
    value: null,
    name: {
      type: String,
      required: true
    },
    prop: {
      type: String
    },
    base: {
      type: String,
      default: ''
    },
    contentCSS: {
      type: String,
      default: ''
    },
    contentStyle: {
      type: String,
      default: ''
    },
    bodyClass: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    onChange: {
      type: Function,
      default: () => {}
    },
    imgUpload: {
      type: Function,
      default: (blobInfo, success, failure) => {
        const img = 'data:image/jpeg;base64,' + blobInfo.base64()
        success(img)
      }
    },
    menubar: {
      type: Boolean,
      default: true
    },
    exConf: {
      type: Object,
      default: null
    },
    plugins: {
      type: [String, Array],
      default: 'advlist autolink lists link image charmap print preview hr anchor pagebreak' +
        ' searchreplace wordcount visualblocks visualchars code fullscreen fullpage autoresize' +
        ' insertdatetime media nonbreaking noneditable save table directionality' +
        ' template paste textpattern imagetools toc help emoticons'
    },
    toolbar: {
      type: [String, Array],
      default: 'fullscreen response-preview' +
      ' | undo redo' +
      ' | linkbtn branchbtn link image' +
      ' | styleselect' +
      ' | bold italic' +
      ' | alignleft aligncenter alignright alignjustify' +
      ' | bullist numlist outdent indent' +
      ' | forecolor backcolor emoticons'
    },
    hasTrans: {
      type: Boolean,
      default: false
    },
    isTransField: {
      type: Boolean,
      default: false
    },
    transField: {
      type: Object,
      default: () => {
        return null
      }
    },
    oriData: null,
    transData: {
      type: Array,
      default: () => []
    },
    showPlaceholder: {
      type: Boolean,
      default: false
    },
    placeholderText: {
      type: String,
      default: null
    },
    grammarConfig: {
      type: Object
    },
    showAutoTranslate: {
      type: Boolean,
      default: false
    }
  },
  setup (props, { emit }) {
    // NOTE：getCurrentInstance 在 vue3 中不建議使用，建議使用 provide/inject 或其他方式來傳遞資料
    // refs: https://v3.ru.vuejs.org/api/composition-api.html#getcurrentinstance
    const vm = getCurrentInstance()
    const val = ref(props.value)
    const showTrans = ref(false)
    const checkTransAll = ref(false)
    const isTransIndeterminate = ref(false)
    const activeNames = ref('')
    const initConf = ref({
      skin_url: `${baseUrl}/skins/ui/oxide`,
      content_css: `${baseUrl}/skins/content/default/content.css`,
      plugins: props.plugins,
      toolbar: props.toolbar,
      menubar: props.menubar,
      images_upload_handler: props.imgUpload,
      height: 400,
      branding: false,
      convert_urls: false,
      keep_styles: false,
      protect: [
        /\{\{(?!\.RowSpan).*?\}\}/g
      ],
      // forced_root_block: 'p',
      // extended_valid_elements: '-p',
      // force_p_newlines: false,
      // force_br_newlines: false,
      noneditable_editable_class: 'btn',
      emoticons_database_url: `${baseUrl}/plugins/emoticons/js/emojis.min.js`,
      external_plugins: {
        'funnowblog': `${baseUrl}/plugins/funnowblog/plugin.js`
        // 'powerpaste': `${baseUrl}/plugins/powerpaste/plugin.min.js`
      },
      cache_suffix: '?v=20201015',
      funnowblog_branch_handler: fetchBranch,
      funnowblog_template_handler: fetchTemplate,
      funnowblog_template_list_handler: fetchTemplateList,
      // powerpaste_allow_local_images: true,
      // powerpaste_word_import: 'prompt',
      // powerpaste_html_import: 'prompt',
      paste_data_images: true
    })

    const {
      value: grammarResult,
      loading: isLoadingGrammar,
      isInvalid: isGrammarCheckerInvalid,
      isError: isGrammarCheckerError,
      resetGrammarChecker,
      fetchGrammarChecker
    } = useGrammarChecker(computed(() => props.grammarConfig || defaultGrammarConfig))
    const formattedGrammarResult = computed(() => {
      if (grammarResult.value) {
        return grammarResult
      }
      if (isLoadingGrammar.value) {
        return vm.proxy.$i18n.t('textarea_generating_data_hint')
      }
      if (isGrammarCheckerError.value) {
        return vm.proxy.$i18n.t('alert_data_unavailable')
      }
      return ''
    })
    const showGrammarChecker = computed(() => !!props.grammarConfig)
    const grammarEditorConfig = computed(() => ({
      ...initConf.value,
      content_style: 'body{background: #ECF5FF}'
    }))

    const {
      value: translationMap,
      isError: isTranslationError,
      loading: isLoadingTranslation,
      fetchTranslation,
      getTranslationByLangCode
    } = useTranslationGenerator(computed(() => ({ content: val.value })))

    // FIXME: 直接修改 props 欄位違反 vue 的資料流，修改時候記得避免不斷 emit transData 造成換面卡頓
    async function onGenerateTranslation () {
      await fetchTranslation()
      if (!Object.values(translationMap.value).length) {
        return
      }

      const updatedField = props.name
      props.transData.forEach(lang => {
        const result = getTranslationByLangCode(lang.code)

        if (result) {
          lang[updatedField] = result
          lang[getStatusField(updatedField)] = 1
        }
      })
    }
    function onInput () {
      emit('input', val.value)
    }

    function onApplyGrammar () {
      val.value = grammarResult.value
      onInput()
      resetGrammarChecker()
    }

    return {
      val,
      showTrans,
      checkTransAll,
      isTransIndeterminate,
      activeNames,
      initConf,
      showGrammarChecker,
      formattedGrammarResult,
      isLoadingGrammar,
      isLoadingTranslation,
      isGrammarCheckerInvalid,
      isGrammarCheckerError,
      isTranslationError,
      grammarEditorConfig,
      fetchGrammarChecker,
      onInput,
      onApplyGrammar,
      onGenerateTranslation
    }
  },
  computed: {
    placeholder () {
      if (!this.showPlaceholder) {
        return ''
      }

      if (this.placeholderText !== null) {
        return this.placeholderText
      }

      return this.getTransText(this.name, 'placeholder', this.base)
    },
    isTransNeedReview () {
      let res = false
      for (let idx in this.transData) {
        let s = this.transData[idx][this.getStatusField(this.name)]
        if (s === undefined || s === 0) {
          res = true
        }
      }
      return res
    }
  },
  created () {
    let conf = {}
    let lang = this.$store.getters.language.replace('-', '_')
    switch (lang) {
      case 'ja':
      case 'zh_TW':
        conf.language = lang
        conf.language_url = `${baseUrl}/langs/${lang}.js`
        break
    }
    if (this.contentCSS) {
      conf.content_css = `${baseUrl}/css/${this.contentCSS}`
    }

    if (this.bodyClass) {
      conf.body_class = this.bodyClass
    }
    this.initConf = Object.assign({}, this.exConf, this.initConf, conf)
  },
  mounted () {
    tinymce.init({})
  },
  methods: {
    getTransText,
    getStatusField,
    onInput () {
      this.$emit('input', this.val)
    },
    onTransChange () {
      this.setTransStatus()
      this.onChange()
    },
    checkEmptyValue (v) {
      return (v === '' || v === null || v === undefined)
    },
    setTransStatus (isUpdateVal) {
      if (this.hasTrans) {
        let sf = this.getStatusField(this.name)
        if (this.checkEmptyValue(this.val)) {
          for (let idx in this.transData) {
            let item = this.transData[idx]
            if (this.checkEmptyValue(item[this.name])) {
              this.$set(item, sf, 1)
            }
          }
          this.handleCheckTransChange()
        } else if (this.val !== this.oriData[this.name] && isUpdateVal) {
          for (let idx in this.transData) {
            let item = this.transData[idx]
            this.$set(item, sf, 0)
          }
          this.handleCheckTransChange()
        }
      }
    },
    toggleTrans () {
      this.showTrans = !this.showTrans
      if (this.showTrans) {
        this.handleCheckTransChange()
      }
    },
    handleCheckTransAllChange (val) {
      let v = val ? 1 : 0
      let n = this.getStatusField(this.name)
      for (let idx in this.transData) {
        this.$set(this.transData[idx], n, v)
      }
      this.isTransIndeterminate = false
    },
    handleCheckTransChange () {
      let uncheckedCount = 0
      let n = this.getStatusField(this.name)
      for (let idx in this.transData) {
        let s = this.transData[idx][n]
        if (s === 0 || s === undefined) {
          uncheckedCount++
        }
      }
      this.checkTransAll = (uncheckedCount === 0)
      this.isTransIndeterminate = uncheckedCount > 0 && uncheckedCount < this.transData.length
    }
  },
  watch: {
    value (newValue) {
      this.val = newValue
      this.setTransStatus(true)
    },
    transData: {
      handler: function () {
        this.setTransStatus()
      },
      deep: true,
      immediate: true
    }
  }
}
</script>
<style lang="scss" scoped>
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0;
  button {
    margin: 0;
  }
  .trans-checked {
    margin-left: auto;
  }
}
.grammar-checker-result__content {
  margin: 10px 0;
  background: #ecf5ff;
  padding: 10px 16px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  color: #606266;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.apply-grammar-btn {
  margin-top: 10px;
  margin-bottom: 0;
}

</style>
